import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import FabricWave3D from "../../FabricWave3D";
import { REQUEST_ACCESS_MUTATION } from "../../../graphql/mutations";

export type RequestAccessHandler = (
  email: string
) => Promise<{ success: boolean; message?: string }>;

interface HomeHeroProps {
  onRequestAccess?: RequestAccessHandler;
}

const HomeHero = ({ onRequestAccess }: HomeHeroProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<{
    success?: boolean;
    message?: string;
  } | null>(null);

  const [requestAccess] = useMutation(REQUEST_ACCESS_MUTATION, {
    onCompleted: (data) => {
      setIsSubmitting(false);
      if (data.requestAccess.success) {
        setFormStatus({
          success: true,
          message: "Thank you! We'll be in touch soon.",
        });
        setEmail("");
      } else {
        setFormStatus({
          success: false,
          message:
            data.requestAccess.errors ||
            "Something went wrong. Please try again.",
        });
      }
    },
    onError: (error) => {
      setIsSubmitting(false);
      setFormStatus({
        success: false,
        message: error.message || "Something went wrong. Please try again.",
      });
    },
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setFormStatus(null);

    try {
      if (onRequestAccess) {
        const result = await onRequestAccess(email.trim());
        setIsSubmitting(false);
        setFormStatus({
          success: result.success,
          message:
            result.message ||
            (result.success
              ? "Thank you! We'll be in touch soon."
              : "Something went wrong. Please try again."),
        });
        if (result.success) setEmail("");
      } else {
        await requestAccess({ variables: { email: email.trim() } });
      }
    } catch (err: any) {
      setIsSubmitting(false);
      setFormStatus({
        success: false,
        message: err?.message || "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 py-20">
      {/* 3D Animated Sound Wave Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <FabricWave3D />

        {/* Additional floating elements for depth */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-pink-300/20 to-purple-300/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-300/20 to-blue-300/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/3 right-1/4 w-60 h-60 bg-gradient-to-br from-blue-300/15 to-indigo-300/15 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      {/* Sophisticated brand presentation */}
      <div
        className={`mb-12 transform transition-all duration-1000 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
      >
        {/* Elevated brand tagline */}
        <p className="text-xs font-light tracking-[0.5em] uppercase text-gray-400 dark:text-gray-500 mb-8">
          Artificial Intelligence Meets Wardrobe Wisdom
        </p>
      </div>

      {/* Ultra-sophisticated main heading */}
      <div className="mb-16">
        <h1
          className={`text-5xl md:text-7xl lg:text-8xl font-thin leading-tight mb-6 transform transition-all duration-1000 delay-200 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <span className="text-gray-900 dark:text-white tracking-tight">
            Unlock Your Wardrobe's
          </span>
          <br />
          <span className="italic font-extralight bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
            Hidden Potential
          </span>
        </h1>

        {/* Elegant subtitle */}
        <p
          className={`text-lg md:text-xl font-light tracking-wide text-gray-500 dark:text-gray-400 transform transition-all duration-1000 delay-300 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          Where forgotten pieces become fashion statements
        </p>
      </div>

      {/* Refined description */}
      <p
        className={`mt-8 text-lg md:text-xl max-w-4xl leading-relaxed font-light text-gray-600 dark:text-gray-300 tracking-wide transform transition-all duration-1000 delay-400 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
      >
        Rediscover the treasures already in your closet. Our AI atelier
        orchestrates intelligent outfit generation from the pieces you
        own—curating elevated looks for every occasion—while bringing order to
        wardrobe management with capsules, rotations, and mindful reuse.
      </p>

      {/* Sophisticated waitlist form */}
      <div
        className={`mt-16 transform transition-all duration-1000 delay-600 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
      >
        <p className="text-sm font-light tracking-[0.2em] uppercase text-gray-400 dark:text-gray-500 mb-6">
          Join the Exclusive Preview
        </p>

        <form
          className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto w-150"
          onSubmit={handleSubmit}
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="px-8 py-4 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 w-full text-gray-900 dark:text-white focus:border-pink-400 focus:ring-2 focus:ring-pink-200/50 dark:focus:ring-pink-800/50 transition-all duration-300 text-base font-light tracking-wide placeholder:text-gray-400 dark:placeholder:text-gray-500"
            required
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-gradient-to-r whitespace-nowrap from-gray-900 to-gray-700 dark:from-white dark:to-gray-200 hover:from-gray-800 hover:to-gray-600 dark:hover:from-gray-100 dark:hover:to-gray-300 text-white dark:text-gray-900 font-light px-10 py-4 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-base tracking-wide disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {isSubmitting ? "Submitting..." : "Reserve Access"}
          </button>
        </form>

        {formStatus && (
          <div
            className={`mt-6 text-sm font-light tracking-wide ${
              formStatus.success
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {formStatus.message}
          </div>
        )}

        <div className="w-24 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent mx-auto mt-12" />
      </div>

      {/* Refined scroll indicator */}
      <div className="absolute bottom-12 animate-bounce">
        <div className="flex flex-col items-center space-y-2">
          <div className="w-px h-8 bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-600 to-transparent" />
          <svg
            className="w-5 h-5 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
